import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Flag, Trophy, Target, Users, MapPin, Barbell, ArrowRight,
  Play, Pause, ShareNetwork, Sparkle,
} from "@phosphor-icons/react";

/*
  /promo — a shareable, video-like landing page for advertising Fairway.
  ---------------------------------------------------------------------
  Plays as a 60-second auto-advancing sequence of app mockups + speech
  bubbles. Designed to be:

    • Screen-recorded on a phone (iOS Control Centre / Android Quick
      Settings → Record) to produce an MP4 for TikTok / Reels / Shorts /
      Google Ads with zero editing.
    • Shared directly as a URL (WhatsApp, Instagram bio, X, Facebook,
      Reddit, forums) — hits the play button immediately.
    • Paused / rewound with tap so users can read speech bubbles at
      their own pace.

  All mockups are hand-crafted CSS — they mimic the actual Fairway app
  styling (colour tokens from /app/frontend/src/index.css) so viewers
  see accurate previews without needing real screen recordings.
*/

const SCENES = [
  {
    id: "hero",
    duration: 5500,
    label: "Meet Fairway",
    accent: "#e6d59a",
    mockup: "hero",
    bubbles: [
      { text: "The pocket caddie for UK, Spain & Portugal ⛳", position: "center", delay: 900 },
    ],
  },
  {
    id: "gps",
    duration: 7500,
    label: "Live GPS on-course",
    accent: "#4ADE80",
    mockup: "gps",
    bubbles: [
      { text: "Exact yardage to every pin — 3,500+ courses", position: "tl", delay: 500 },
      { text: "Front / middle / back — plus live wind & pin freshness", position: "br", delay: 2800 },
    ],
  },
  {
    id: "score",
    duration: 7500,
    label: "Score without thinking",
    accent: "#C4543C",
    mockup: "score",
    bubbles: [
      { text: "Just tap your score — we handle the maths", position: "tl", delay: 400 },
      { text: "Stableford, Match Play & Net — all auto-calculated", position: "br", delay: 2800 },
    ],
  },
  {
    id: "fourball",
    duration: 6500,
    label: "Fourball with mates",
    accent: "#e6d59a",
    mockup: "fourball",
    bubbles: [
      { text: "Share a link so mates score live on their own phone", position: "tl", delay: 400 },
      { text: "…or pass one phone around — either way, per-player handicaps", position: "br", delay: 2800 },
    ],
  },
  {
    id: "leagues",
    duration: 6500,
    label: "Leagues & Ryder-Cup seasons",
    accent: "#4ADE80",
    mockup: "leagues",
    bubbles: [
      { text: "Private mate leagues — join by code or link", position: "tl", delay: 500 },
      { text: "Season-long team seasons, Ryder-Cup style", position: "br", delay: 2800 },
    ],
  },
  {
    id: "swing",
    duration: 6500,
    label: "Swing Lab (Pro)",
    accent: "#e6d59a",
    mockup: "swing",
    bubbles: [
      { text: "Film your swing — AI pose-skeleton overlay", position: "tl", delay: 500 },
      { text: "Tempo + posture feedback, right on your phone", position: "br", delay: 2800 },
    ],
  },
  {
    id: "watch",
    duration: 6500,
    label: "On your wrist",
    accent: "#4ADE80",
    mockup: "watch",
    bubbles: [
      { text: "Distances live on your smartwatch — phone stays in the bag", position: "tl", delay: 500 },
      { text: "Score, hole info, next tee — glance and go", position: "br", delay: 2800 },
    ],
  },
  {
    id: "handicap",
    duration: 7500,
    label: "Your Handicap Index",
    accent: "#C4543C",
    mockup: "handicap",
    bubbles: [
      { text: "Play 3 rounds → your Handicap Index unlocks automatically", position: "tl", delay: 500 },
      { text: "Plus a course handicap for every layout, based on Rating & Slope", position: "br", delay: 2800 },
    ],
  },
  {
    id: "cta",
    duration: 6500,
    label: "Play smarter with Fairway",
    accent: "#e6d59a",
    mockup: "cta",
    bubbles: [],
  },
];

const TOTAL_MS = SCENES.reduce((a, s) => a + s.duration, 0);

// ────────────────────────────────────────────────────────────────
// Individual phone-screen mockups (styled to match real app UI)
// ────────────────────────────────────────────────────────────────

const PhoneFrame = ({ children, accent = "#e6d59a" }) => (
  <div
    className="relative mx-auto"
    style={{
      width: "min(320px, 78vw)",
      aspectRatio: "9/19",
      background: "#0b1512",
      borderRadius: "44px",
      padding: "10px",
      boxShadow:
        `0 0 0 2px rgba(255,255,255,.06), 0 30px 80px -20px rgba(0,0,0,.7), 0 0 80px -10px ${accent}30`,
      transition: "box-shadow .6s ease",
    }}
  >
    <div
      className="w-full h-full overflow-hidden relative"
      style={{ borderRadius: "36px", background: "#F2EEE7" }}
    >
      {/* notch */}
      <div
        className="absolute left-1/2 -translate-x-1/2 top-2 z-10"
        style={{
          width: "80px", height: "22px",
          background: "#0b1512", borderRadius: "999px",
        }}
      />
      {children}
    </div>
  </div>
);

const HeroMockup = () => (
  <div className="w-full h-full flex flex-col items-center justify-center px-5 text-center relative overflow-hidden">
    <div className="absolute inset-0" style={{
      background: "radial-gradient(circle at 50% 30%, rgba(20,61,47,.09), transparent 60%)",
    }} />
    {/* Soft golf-course silhouette at bottom */}
    <svg className="absolute bottom-0 left-0 right-0 pointer-events-none" viewBox="0 0 200 60" preserveAspectRatio="none" style={{ width: "100%", height: 70, opacity: .35 }}>
      <path d="M0 45 Q 30 30 60 40 T 120 35 T 200 42 L 200 60 L 0 60 Z" fill="#143D2F" />
      <path d="M0 52 Q 40 42 80 50 T 160 46 T 200 50 L 200 60 L 0 60 Z" fill="#0b2e21" />
    </svg>
    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-3 relative" style={{ background: "#143D2F", boxShadow: "0 12px 30px -8px rgba(20,61,47,.5)" }}>
      <Flag size={30} weight="fill" style={{ color: "#e6d59a" }} />
      <div className="absolute -top-1 -right-1" style={{ animation: "fw-float 3s ease-in-out infinite" }}>
        <Sparkle size={14} weight="fill" style={{ color: "#C4543C" }} />
      </div>
    </div>
    <div className="font-black text-3xl tracking-tighter" style={{ color: "#143D2F", fontFamily: "'Inter',system-ui,sans-serif", letterSpacing: "-.03em" }}>
      Fairway
    </div>
    <div className="text-[10px] uppercase tracking-[.22em] mt-1" style={{ color: "#5C5C5C" }}>
      UK · Spain · Portugal
    </div>
    <div className="mt-4 font-black text-[22px] leading-[1.1]" style={{ color: "#143D2F", fontFamily: "'Inter',system-ui,sans-serif", letterSpacing: "-.03em" }}>
      Play better.<br />Beat your mates.
    </div>
    <div className="text-xs mt-2 max-w-[22ch] leading-snug" style={{ color: "#5C5C5C" }}>
      GPS, scoring, leagues & Swing AI — all in one app.
    </div>
    {/* Trust pills */}
    <div className="flex flex-wrap gap-1.5 justify-center mt-4 relative z-10">
      <span className="px-2 py-1 rounded-full text-[10px] font-black" style={{ background: "#EADEC8", color: "#143D2F" }}>3,500+ courses</span>
      <span className="px-2 py-1 rounded-full text-[10px] font-black" style={{ background: "#EADEC8", color: "#143D2F" }}>Free forever</span>
      <span className="px-2 py-1 rounded-full text-[10px] font-black" style={{ background: "#EADEC8", color: "#143D2F" }}>No card needed</span>
    </div>
  </div>
);

const GpsMockup = () => (
  <div className="w-full h-full relative">
    {/* Fake satellite map */}
    <div className="absolute inset-0" style={{
      background: "linear-gradient(155deg, #3d5a2c 0%, #4a6e33 20%, #5a8340 40%, #6b9a4d 60%, #4a6e33 80%, #34472a 100%)",
    }}>
      {/* Fairway curves */}
      <div className="absolute inset-0" style={{
        background: `radial-gradient(ellipse 80% 30% at 30% 40%, #8ab558 0%, transparent 55%),
                     radial-gradient(ellipse 60% 20% at 65% 65%, #7aa848 0%, transparent 55%),
                     radial-gradient(circle 15% at 75% 30%, #a5c470 0%, transparent 60%)`,
      }} />
      {/* Bunkers */}
      <div className="absolute" style={{ top: "30%", left: "48%", width: 24, height: 14, borderRadius: "50%", background: "#e8d9a8", opacity: .85 }} />
      <div className="absolute" style={{ top: "62%", left: "40%", width: 18, height: 12, borderRadius: "50%", background: "#e8d9a8", opacity: .85 }} />
      {/* Green + pin */}
      <div className="absolute" style={{ top: "28%", left: "70%", width: 42, height: 42, borderRadius: "50%", background: "#b6d976", boxShadow: "inset 0 0 8px rgba(0,0,0,.15)" }} />
      <div className="absolute" style={{ top: "26%", left: "82%", width: 2, height: 22, background: "#fff" }} />
      <div className="absolute" style={{ top: "26%", left: "83%", width: 10, height: 8, background: "#C4543C", clipPath: "polygon(0 0, 100% 50%, 0 100%)" }} />
      {/* Ball position */}
      <div className="absolute" style={{ top: "72%", left: "22%" }}>
        <div className="w-4 h-4 rounded-full bg-white shadow-md" style={{ boxShadow: "0 0 0 3px #C4543C, 0 0 20px rgba(196,84,60,.6)" }} />
        <div className="absolute" style={{ inset: -6, borderRadius: "50%", border: "2px solid rgba(196,84,60,.4)", animation: "fw-ping 1.6s ease-out infinite" }} />
      </div>
      {/* Distance line */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
        <line x1="22" y1="72" x2="83" y2="28" stroke="#fff" strokeWidth="0.5" strokeDasharray="1.5,1.5" opacity=".8" />
      </svg>
    </div>
    {/* Top strip */}
    <div className="absolute top-6 left-3 right-3 flex items-center justify-between text-white text-[10px] font-bold z-10">
      <div className="px-2 py-1 rounded-full backdrop-blur-md" style={{ background: "rgba(20,61,47,.75)" }}>HOLE 7 · PAR 4</div>
      <div className="px-2 py-1 rounded-full backdrop-blur-md" style={{ background: "rgba(20,61,47,.75)" }}>WHITE · 412y</div>
    </div>
    {/* Big yardage */}
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center">
      <div className="text-[10px] uppercase tracking-widest text-white/85 drop-shadow-md mb-1">To pin</div>
      <div className="text-white font-black leading-none drop-shadow-lg" style={{ fontSize: "64px", letterSpacing: "-.05em", fontVariantNumeric: "tabular-nums" }}>
        <span style={{ background: "linear-gradient(180deg,#fff,#e6d59a)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>142</span>
      </div>
      <div className="text-white/85 text-xs font-bold tracking-wider">YARDS</div>
      <div className="flex gap-1.5 justify-center mt-2 text-[10px] font-bold">
        <span className="px-2 py-0.5 rounded-full text-white" style={{ background: "rgba(20,61,47,.75)" }}>F 128</span>
        <span className="px-2 py-0.5 rounded-full text-white" style={{ background: "rgba(20,61,47,.75)" }}>M 142</span>
        <span className="px-2 py-0.5 rounded-full text-white" style={{ background: "rgba(20,61,47,.75)" }}>B 156</span>
      </div>
    </div>
  </div>
);

const ScoreMockup = () => (
  <div className="w-full h-full pt-8 px-4 flex flex-col" style={{ background: "#F2EEE7" }}>
    <div className="flex items-center justify-between mb-3">
      <div>
        <div className="text-[9px] uppercase tracking-widest text-gray-500">Round · Sunningdale</div>
        <div className="font-black text-lg leading-tight" style={{ color: "#143D2F" }}>Hole 12 · Par 4</div>
      </div>
      <div className="flex items-center gap-1 px-2.5 py-1 rounded-full" style={{ background: "#EADEC8", border: "1px solid rgba(20,61,47,.15)" }}>
        <Target size={12} weight="fill" style={{ color: "#C4543C" }} />
        <span className="font-black text-xs" style={{ color: "#143D2F", fontVariantNumeric: "tabular-nums" }}>14.3</span>
        <span className="text-[8px] uppercase tracking-wider opacity-70" style={{ color: "#143D2F" }}>HCP</span>
      </div>
    </div>
    {/* Score buttons */}
    <div className="grid grid-cols-3 gap-1.5 mb-2">
      {[3, 4, 5].map((n, i) => (
        <div key={n} className={`aspect-square rounded-xl flex items-center justify-center font-black text-2xl ${i === 1 ? "text-white" : ""}`}
             style={{
               background: i === 1 ? "#143D2F" : "#fff",
               color: i === 1 ? "#fff" : "#143D2F",
               boxShadow: i === 1 ? "0 6px 18px -4px rgba(20,61,47,.5)" : "inset 0 0 0 1px rgba(20,61,47,.12)",
             }}>
          {n}
        </div>
      ))}
    </div>
    <div className="grid grid-cols-2 gap-1.5">
      <div className="p-2 rounded-xl" style={{ background: "#fff", boxShadow: "inset 0 0 0 1px rgba(20,61,47,.08)" }}>
        <div className="text-[9px] uppercase tracking-wider text-gray-500">Putts</div>
        <div className="font-black text-lg" style={{ color: "#143D2F" }}>2</div>
      </div>
      <div className="p-2 rounded-xl" style={{ background: "#fff", boxShadow: "inset 0 0 0 1px rgba(20,61,47,.08)" }}>
        <div className="text-[9px] uppercase tracking-wider text-gray-500">Stableford</div>
        <div className="font-black text-lg" style={{ color: "#C4543C" }}>+2</div>
      </div>
    </div>
    <div className="mt-3 p-2.5 rounded-xl" style={{ background: "#143D2F", color: "#fff" }}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider opacity-80">
        <span>Match play</span>
        <span>Hole 12/18</span>
      </div>
      <div className="font-black text-base mt-0.5" style={{ letterSpacing: "-.01em" }}>You 2 UP · Dave 2 DN</div>
    </div>
    <div className="mt-auto mb-2 p-2 rounded-xl" style={{ background: "rgba(20,61,47,.06)" }}>
      <div className="flex items-center justify-between text-[10px]" style={{ color: "#143D2F" }}>
        <span className="opacity-70">Points to par</span>
        <span className="font-black" style={{ fontVariantNumeric: "tabular-nums" }}>+3 / 24 pts</span>
      </div>
    </div>
  </div>
);

const FourballMockup = () => (
  <div className="w-full h-full pt-8 px-4" style={{ background: "#F2EEE7" }}>
    <div className="text-[9px] uppercase tracking-widest text-gray-500">Fourball · Hole 8</div>
    <div className="font-black text-lg mb-3" style={{ color: "#143D2F" }}>Whose turn?</div>
    {[
      { name: "You", hcp: "14.3", strokes: 4, dist: "furthest · 168y", active: true },
      { name: "Dave", hcp: "8.2", strokes: 4, dist: "on the green", active: false },
      { name: "Jamie", hcp: "22.1", strokes: 5, dist: "in the rough · 82y", active: false },
      { name: "Sam", hcp: "11.7", strokes: 3, dist: "just short · 34y", active: false },
    ].map((p, i) => (
      <div key={i} className="flex items-center gap-2 p-2 mb-1.5 rounded-xl"
           style={{
             background: p.active ? "#EADEC8" : "#fff",
             boxShadow: p.active ? "0 0 0 2px #C4543C, 0 4px 12px -4px rgba(196,84,60,.4)" : "inset 0 0 0 1px rgba(20,61,47,.08)",
           }}>
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs"
             style={{ background: p.active ? "#C4543C" : "#143D2F" }}>
          {p.name.slice(0, 1)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-black text-sm" style={{ color: "#143D2F" }}>{p.name}</span>
            <span className="text-[9px] px-1 py-0.5 rounded" style={{ background: "rgba(20,61,47,.08)", color: "#143D2F", fontVariantNumeric: "tabular-nums" }}>{p.hcp}</span>
          </div>
          <div className="text-[10px] text-gray-500 truncate">{p.dist}</div>
        </div>
        <div className="font-black text-lg" style={{ color: p.active ? "#C4543C" : "#143D2F", fontVariantNumeric: "tabular-nums" }}>
          {p.strokes}
        </div>
      </div>
    ))}
    <div className="mt-3 p-3 rounded-xl text-center" style={{ background: "#143D2F", color: "#fff" }}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">Next to play</div>
      <div className="font-black text-lg">You · furthest from pin</div>
      <div className="text-[10px] opacity-70 mt-1">Live-synced across all mates&apos; phones</div>
    </div>
  </div>
);

const LeaguesMockup = () => (
  <div className="w-full h-full pt-8 px-4" style={{ background: "#F2EEE7" }}>
    <div className="text-[9px] uppercase tracking-widest text-gray-500">Season · 2026</div>
    <div className="font-black text-lg" style={{ color: "#143D2F" }}>The Sunday Cup</div>
    <div className="text-[10px] text-gray-500 mb-3">🏴󠁧󠁢󠁥󠁮󠁧󠁿 Reds vs Blues · Ryder-Cup format</div>

    {/* Score header */}
    <div className="grid grid-cols-2 gap-2 mb-3">
      <div className="p-3 rounded-xl text-center" style={{ background: "#C4543C", color: "#fff" }}>
        <div className="text-[9px] uppercase tracking-wider opacity-80">Reds</div>
        <div className="font-black text-3xl leading-none" style={{ fontVariantNumeric: "tabular-nums" }}>7½</div>
      </div>
      <div className="p-3 rounded-xl text-center" style={{ background: "#143D2F", color: "#fff" }}>
        <div className="text-[9px] uppercase tracking-wider opacity-80">Blues</div>
        <div className="font-black text-3xl leading-none" style={{ fontVariantNumeric: "tabular-nums" }}>6½</div>
      </div>
    </div>

    <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Top scorers</div>
    {[
      { name: "Dave", team: "R", pts: "3.0", trend: "+0.5" },
      { name: "You", team: "B", pts: "2.5", trend: "+1.0" },
      { name: "Sam", team: "R", pts: "2.5", trend: "+0.0" },
      { name: "Jamie", team: "B", pts: "2.0", trend: "+0.5" },
    ].map((p, i) => (
      <div key={i} className="flex items-center gap-2 p-1.5 mb-1 rounded-lg" style={{ background: "#fff", boxShadow: "inset 0 0 0 1px rgba(20,61,47,.06)" }}>
        <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
             style={{ background: p.team === "R" ? "#C4543C" : "#143D2F" }}>
          {p.team}
        </div>
        <div className="flex-1 text-xs font-bold" style={{ color: "#143D2F" }}>{p.name}</div>
        <div className="text-xs font-black" style={{ color: "#143D2F", fontVariantNumeric: "tabular-nums" }}>{p.pts}</div>
        <div className="text-[9px] font-bold" style={{ color: "#10B981", fontVariantNumeric: "tabular-nums" }}>{p.trend}</div>
      </div>
    ))}

    <div className="mt-2 p-2 rounded-xl text-[10px] text-center" style={{ background: "rgba(196,84,60,.1)", color: "#C4543C" }}>
      🏆 3 rounds left · come on the Blues
    </div>
  </div>
);

const SwingMockup = () => (
  <div className="w-full h-full relative overflow-hidden" style={{ background: "#0f1a17" }}>
    {/* Video-like background */}
    <div className="absolute inset-0" style={{
      background: `linear-gradient(180deg, #253c33 0%, #1a2d24 50%, #0f1a17 100%)`,
    }}>
      {/* Ground line */}
      <div className="absolute bottom-1/4 left-0 right-0 h-px" style={{ background: "rgba(255,255,255,.15)" }} />
    </div>
    {/* Pose skeleton (golfer stick figure) */}
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 190" preserveAspectRatio="xMidYMid meet">
      <g stroke="#4ADE80" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity=".9" style={{ filter: "drop-shadow(0 0 4px #4ADE80)" }}>
        {/* head */}
        <circle cx="52" cy="60" r="4" />
        {/* spine */}
        <line x1="52" y1="64" x2="50" y2="100" />
        {/* shoulders */}
        <line x1="45" y1="72" x2="58" y2="70" />
        {/* arms — mid-swing */}
        <line x1="45" y1="72" x2="35" y2="82" />
        <line x1="35" y1="82" x2="30" y2="94" />
        <line x1="58" y1="70" x2="66" y2="82" />
        <line x1="66" y1="82" x2="72" y2="92" />
        {/* club */}
        <line x1="72" y1="92" x2="88" y2="118" strokeWidth="1.6" />
        <circle cx="88" cy="118" r="1.8" fill="#e6d59a" stroke="none" />
        {/* hips */}
        <line x1="47" y1="100" x2="55" y2="100" />
        {/* legs */}
        <line x1="47" y1="100" x2="44" y2="130" />
        <line x1="44" y1="130" x2="48" y2="150" />
        <line x1="55" y1="100" x2="58" y2="130" />
        <line x1="58" y1="130" x2="54" y2="150" />
      </g>
      {/* joint dots */}
      <g fill="#e6d59a" style={{ filter: "drop-shadow(0 0 3px #e6d59a)" }}>
        {[[52,60],[45,72],[58,70],[35,82],[30,94],[66,82],[72,92],[50,100],[47,100],[55,100],[44,130],[58,130],[48,150],[54,150]].map(([x,y],i)=>(
          <circle key={i} cx={x} cy={y} r="1.4" />
        ))}
      </g>
    </svg>
    {/* HUD */}
    <div className="absolute top-6 left-3 right-3 flex items-center justify-between text-white text-[10px] font-bold">
      <div className="px-2 py-1 rounded-full backdrop-blur-md flex items-center gap-1" style={{ background: "rgba(0,0,0,.5)" }}>
        <Barbell size={11} weight="fill" /> SWING LAB
      </div>
      <div className="flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#C4543C" }} /> REC
      </div>
    </div>
    <div className="absolute bottom-4 left-3 right-3 space-y-1.5">
      <div className="p-2 rounded-lg text-[11px] backdrop-blur-md" style={{ background: "rgba(20,61,47,.75)", color: "#fff" }}>
        <div className="flex items-center justify-between">
          <span className="opacity-80">Tempo ratio</span>
          <span className="font-black" style={{ color: "#4ADE80", fontVariantNumeric: "tabular-nums" }}>3.1 : 1 ✓</span>
        </div>
      </div>
      <div className="p-2 rounded-lg text-[11px] backdrop-blur-md" style={{ background: "rgba(20,61,47,.75)", color: "#fff" }}>
        <div className="flex items-center justify-between">
          <span className="opacity-80">Spine angle</span>
          <span className="font-black" style={{ color: "#F59E0B", fontVariantNumeric: "tabular-nums" }}>38° · early extend</span>
        </div>
      </div>
    </div>
  </div>
);

const HandicapMockup = () => {
  // A downward-trending sparkline — golfers love seeing their index drop.
  const points = [17.4, 17.1, 16.8, 16.9, 16.2, 15.8, 15.3, 15.1, 14.7, 14.5, 14.3];
  const w = 260, h = 60, pad = 6;
  const min = Math.min(...points), max = Math.max(...points);
  const xStep = (w - pad * 2) / (points.length - 1);
  const yFor = (v) => pad + ((max - v) / (max - min || 1)) * (h - pad * 2);
  const path = points.map((v, i) => `${i === 0 ? "M" : "L"} ${pad + i * xStep} ${yFor(v)}`).join(" ");
  const areaPath = `${path} L ${pad + (points.length - 1) * xStep} ${h - pad} L ${pad} ${h - pad} Z`;

  return (
    <div className="w-full h-full pt-8 px-4" style={{ background: "#F2EEE7" }}>
      <div className="text-[9px] uppercase tracking-widest text-gray-500">Your Handicap Index</div>
      <div className="flex items-baseline gap-2">
        <div className="font-black leading-none" style={{ color: "#143D2F", fontSize: "44px", letterSpacing: "-.03em", fontVariantNumeric: "tabular-nums" }}>
          14.3
        </div>
        <div className="flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: "rgba(16,185,129,.15)" }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="#10B981"><path d="M12 20l-8-8h5V4h6v8h5z" /></svg>
          <span className="text-[10px] font-black" style={{ color: "#10B981", fontVariantNumeric: "tabular-nums" }}>-3.1 · 6 mo</span>
        </div>
      </div>

      {/* Sparkline */}
      <div className="mt-2 p-2 rounded-xl" style={{ background: "#fff", boxShadow: "inset 0 0 0 1px rgba(20,61,47,.08)" }}>
        <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
          <defs>
            <linearGradient id="fw-spark" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#143D2F" stopOpacity=".2" />
              <stop offset="100%" stopColor="#143D2F" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill="url(#fw-spark)" />
          <path d={path} fill="none" stroke="#143D2F" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          {/* End dot */}
          <circle cx={pad + (points.length - 1) * xStep} cy={yFor(points[points.length - 1])} r="3.5" fill="#C4543C" />
        </svg>
        <div className="flex items-center justify-between text-[9px] mt-0.5" style={{ color: "#5C5C5C" }}>
          <span>May</span><span>Jun</span><span>Jul</span><span>Aug</span><span>Sep</span><span>Oct</span>
        </div>
      </div>

      {/* Course handicap at current course */}
      <div className="mt-3 p-2.5 rounded-xl" style={{ background: "#143D2F", color: "#fff" }}>
        <div className="text-[9px] uppercase tracking-widest opacity-70">Playing today · Sunningdale</div>
        <div className="flex items-baseline justify-between mt-0.5">
          <div className="font-black text-xl leading-none" style={{ letterSpacing: "-.02em" }}>15 shots</div>
          <div className="text-[10px] opacity-80">CR 71.2 · Slope 132</div>
        </div>
        <div className="text-[10px] opacity-70 mt-1">Your course handicap · WHS allocation live</div>
      </div>

      {/* Recent scoring diff cards */}
      <div className="mt-2 text-[10px] uppercase tracking-widest text-gray-500">Best 8 of last 20</div>
      <div className="flex gap-1 mt-1">
        {[13.8, 14.1, 14.4, 14.7, 15.1, 15.3, 15.9, 16.2].map((d, i) => (
          <div key={i} className="flex-1 text-center py-1 rounded-md" style={{
            background: i < 3 ? "rgba(196,84,60,.15)" : "#fff",
            boxShadow: "inset 0 0 0 1px rgba(20,61,47,.08)",
          }}>
            <div className="font-black text-[10px]" style={{ color: i < 3 ? "#C4543C" : "#143D2F", fontVariantNumeric: "tabular-nums" }}>
              {d}
            </div>
          </div>
        ))}
      </div>
      <div className="text-[9px] mt-1 text-gray-500 text-center">Lowest 8 differentials → your Index</div>
    </div>
  );
};

const CtaMockup = () => (
  <div className="w-full h-full flex flex-col items-center justify-center px-5 text-center relative overflow-hidden" style={{ background: "linear-gradient(160deg, #143D2F 0%, #0b2e21 100%)" }}>
    <div className="absolute inset-0 opacity-30" style={{
      background: "radial-gradient(circle at 50% 30%, rgba(230,213,154,.4), transparent 60%)",
    }} />
    {/* Floating sparkles */}
    <div className="absolute" style={{ top: "18%", left: "18%", animation: "fw-float 3.2s ease-in-out infinite" }}>
      <Sparkle size={12} weight="fill" style={{ color: "#e6d59a", opacity: .6 }} />
    </div>
    <div className="absolute" style={{ top: "24%", right: "16%", animation: "fw-float 4s ease-in-out infinite .5s" }}>
      <Sparkle size={10} weight="fill" style={{ color: "#e6d59a", opacity: .5 }} />
    </div>
    <div className="absolute" style={{ bottom: "22%", left: "22%", animation: "fw-float 3.6s ease-in-out infinite 1s" }}>
      <Sparkle size={8} weight="fill" style={{ color: "#C4543C", opacity: .5 }} />
    </div>

    {/* Stars */}
    <div className="flex items-center gap-0.5 mb-2 relative z-10">
      {[0, 1, 2, 3, 4].map((i) => (
        <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#e6d59a">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
    <div className="text-[10px] uppercase tracking-widest text-white/70 relative z-10">
      Loved by golfers · UK · Spain · Portugal
    </div>

    <Flag size={36} weight="fill" style={{ color: "#e6d59a" }} className="mt-3 mb-2" />
    <div className="font-black text-[26px] leading-[1.05] text-white relative z-10" style={{ fontFamily: "'Inter',system-ui,sans-serif", letterSpacing: "-.03em" }}>
      Play smarter.<br />Beat your mates.
    </div>

    {/* No card needed pill */}
    <div className="mt-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black relative z-10" style={{ background: "rgba(74,222,128,.2)", color: "#4ADE80", border: "1px solid rgba(74,222,128,.4)" }}>
      <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#4ADE80" }} />
      FREE FOREVER · NO CARD NEEDED
    </div>

    {/* Big CTA */}
    <div className="mt-4 w-full max-w-[240px] py-3 rounded-full font-black text-sm flex items-center justify-center gap-2 relative z-10 shadow-2xl" style={{
      background: "linear-gradient(180deg, #f2e0a6 0%, #e6d59a 100%)",
      color: "#143D2F",
      boxShadow: "0 12px 30px -8px rgba(230,213,154,.5)",
    }}>
      <Sparkle size={14} weight="fill" /> Start playing free <ArrowRight size={14} weight="bold" />
    </div>

    <div className="mt-3 text-[10px] text-white/60 relative z-10">
      fairway-golf.org
    </div>
  </div>
);

const WatchMockup = () => (
  <div className="w-full h-full flex flex-col items-center justify-center relative overflow-hidden" style={{ background: "linear-gradient(180deg, #0e1a15 0%, #050a08 100%)" }}>
    {/* Ambient wrist scene */}
    <div className="absolute inset-0 opacity-40" style={{
      background: "radial-gradient(circle at 50% 50%, rgba(74,222,128,.15) 0%, transparent 55%)",
    }} />

    {/* Watch strap (top + bottom) */}
    <div className="absolute top-0 left-1/2 -translate-x-1/2 rounded-b-3xl" style={{
      width: "80px", height: "38px",
      background: "linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 100%)",
      boxShadow: "inset 0 -2px 4px rgba(0,0,0,.5)",
    }} />
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-t-3xl" style={{
      width: "80px", height: "38px",
      background: "linear-gradient(180deg, #1a1a1a 0%, #2a2a2a 100%)",
      boxShadow: "inset 0 2px 4px rgba(0,0,0,.5)",
    }} />

    {/* Watch body */}
    <div className="relative flex items-center justify-center" style={{
      width: "170px", height: "180px",
      background: "linear-gradient(160deg, #232323 0%, #0a0a0a 100%)",
      borderRadius: "34px",
      boxShadow: "0 0 0 2px #333, 0 20px 40px -8px rgba(0,0,0,.7), inset 0 0 0 3px #111",
    }}>
      {/* Digital crown */}
      <div className="absolute right-[-4px] top-[38%] w-2 h-6 rounded-r" style={{ background: "#666", boxShadow: "inset 0 -2px 2px rgba(0,0,0,.5)" }} />
      <div className="absolute right-[-3px] top-[62%] w-1.5 h-4 rounded-r" style={{ background: "#555" }} />

      {/* Watch face content */}
      <div className="relative flex flex-col items-center justify-center text-center px-2" style={{ width: "146px", height: "156px", borderRadius: "26px", background: "#000", overflow: "hidden" }}>
        {/* Top row */}
        <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Flag size={9} weight="fill" style={{ color: "#e6d59a" }} />
            <span className="text-[8px] font-black text-white/90">H7</span>
          </div>
          <div className="text-[8px] font-black" style={{ color: "#4ADE80", fontVariantNumeric: "tabular-nums" }}>PAR 4</div>
        </div>

        {/* Big yardage */}
        <div className="mt-1">
          <div className="text-[8px] uppercase tracking-widest text-white/50 mb-0.5">To pin</div>
          <div className="font-black leading-none" style={{
            color: "#fff",
            fontSize: "44px",
            letterSpacing: "-.05em",
            fontVariantNumeric: "tabular-nums",
            textShadow: "0 0 20px rgba(230,213,154,.4)",
          }}>142</div>
          <div className="text-[9px] font-bold mt-0.5" style={{ color: "#e6d59a" }}>YARDS</div>
        </div>

        {/* F/M/B chips */}
        <div className="flex gap-1 mt-1.5">
          <span className="px-1.5 py-0.5 rounded text-[8px] font-black" style={{ background: "#1e1e1e", color: "#4ADE80", fontVariantNumeric: "tabular-nums" }}>F 128</span>
          <span className="px-1.5 py-0.5 rounded text-[8px] font-black" style={{ background: "#e6d59a", color: "#0a0a0a", fontVariantNumeric: "tabular-nums" }}>M 142</span>
          <span className="px-1.5 py-0.5 rounded text-[8px] font-black" style={{ background: "#1e1e1e", color: "#4ADE80", fontVariantNumeric: "tabular-nums" }}>B 156</span>
        </div>

        {/* Score strip */}
        <div className="absolute bottom-1.5 left-2 right-2 flex items-center justify-between text-[8px] font-bold">
          <span className="text-white/70">Score</span>
          <span className="px-1.5 py-0.5 rounded" style={{ background: "#C4543C", color: "#fff", fontVariantNumeric: "tabular-nums" }}>+2 · 24 pts</span>
        </div>
      </div>
    </div>

    {/* Subtle "Live" indicator */}
    <div className="absolute top-4 right-4 flex items-center gap-1 z-10">
      <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#C4543C" }} />
      <span className="text-[9px] font-black text-white/70">LIVE</span>
    </div>
  </div>
);

const MOCKUPS = {
  hero: HeroMockup,
  gps: GpsMockup,
  score: ScoreMockup,
  fourball: FourballMockup,
  leagues: LeaguesMockup,
  swing: SwingMockup,
  watch: WatchMockup,
  handicap: HandicapMockup,
  cta: CtaMockup,
};

// ────────────────────────────────────────────────────────────────
// Speech-bubble component
// ────────────────────────────────────────────────────────────────

const bubblePositionClass = (pos) => {
  switch (pos) {
    case "tl": return "top-[14%] left-[4%] sm:left-[10%]";
    case "tr": return "top-[14%] right-[4%] sm:right-[10%]";
    case "bl": return "bottom-[18%] left-[4%] sm:left-[10%]";
    case "br": return "bottom-[18%] right-[4%] sm:right-[10%]";
    case "center":
    default: return "top-[8%] left-1/2 -translate-x-1/2";
  }
};

const bubbleTailClass = (pos) => {
  switch (pos) {
    case "tl": return "before:content-[''] before:absolute before:left-6 before:-bottom-2 before:w-4 before:h-4 before:rotate-45 before:bg-inherit";
    case "tr": return "before:content-[''] before:absolute before:right-6 before:-bottom-2 before:w-4 before:h-4 before:rotate-45 before:bg-inherit";
    case "bl": return "before:content-[''] before:absolute before:left-6 before:-top-2 before:w-4 before:h-4 before:rotate-45 before:bg-inherit";
    case "br": return "before:content-[''] before:absolute before:right-6 before:-top-2 before:w-4 before:h-4 before:rotate-45 before:bg-inherit";
    default: return "";
  }
};

const Bubble = ({ text, position, visible }) => (
  <div
    className={`absolute z-20 pointer-events-none max-w-[240px] sm:max-w-[300px] ${bubblePositionClass(position)}`}
    style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0) scale(1)" : "translateY(10px) scale(.9)",
      transition: "opacity .55s cubic-bezier(.16,1,.3,1), transform .55s cubic-bezier(.16,1,.3,1)",
      ...(position === "center" ? { transform: `translateX(-50%) ${visible ? "translateY(0)" : "translateY(10px)"} scale(${visible ? 1 : .9})` } : {}),
    }}
  >
    <div className={`relative px-3.5 py-2 rounded-2xl font-bold text-[12px] sm:text-[13px] leading-snug bg-white text-[#143D2F] ${bubbleTailClass(position)}`}
         style={{
           boxShadow: "0 20px 40px -12px rgba(0,0,0,.4), 0 0 0 2px #C4543C, 0 0 0 4px rgba(196,84,60,.2)",
         }}>
      {text}
    </div>
  </div>
);

// ────────────────────────────────────────────────────────────────
// Main Promo page
// ────────────────────────────────────────────────────────────────

export default function Promo() {
  const [sceneIdx, setSceneIdx] = useState(0);
  const [sceneStart, setSceneStart] = useState(Date.now());
  const [tick, setTick] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [copied, setCopied] = useState(false);

  const scene = SCENES[sceneIdx];
  const Mockup = MOCKUPS[scene.mockup];

  // Auto-advance loop
  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 100);
    return () => clearInterval(id);
  }, [playing]);

  useEffect(() => {
    if (!playing) return;
    const elapsed = Date.now() - sceneStart;
    if (elapsed >= scene.duration) {
      const next = (sceneIdx + 1) % SCENES.length;
      setSceneIdx(next);
      setSceneStart(Date.now());
    }
  }, [tick, playing, sceneStart, scene.duration, sceneIdx]);

  const elapsedInScene = Math.min(scene.duration, Date.now() - sceneStart);
  const totalElapsedBefore = useMemo(() =>
    SCENES.slice(0, sceneIdx).reduce((a, s) => a + s.duration, 0), [sceneIdx]);
  const overallPct = ((totalElapsedBefore + elapsedInScene) / TOTAL_MS) * 100;

  const jumpTo = (i) => {
    setSceneIdx(i);
    setSceneStart(Date.now());
  };

  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/promo` : "";
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Fairway — the pocket caddie for UK, Spain & Portugal",
          text: "Live GPS, auto-scoring, fourball with mates. Free to try.",
          url: shareUrl,
        });
        return;
      } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-start relative overflow-hidden"
      style={{
        background: `radial-gradient(circle at 50% 20%, #1a4a38 0%, #0b1512 55%, #050a08 100%)`,
        color: "#fff",
        fontFamily: "'Inter',system-ui,-apple-system,Segoe UI,Roboto,sans-serif",
      }}
      data-testid="promo-page"
    >
      {/* Global keyframes */}
      <style>{`
        @keyframes fw-ping { 0% { transform: scale(1); opacity:.9 } 80%,100% { transform: scale(2.4); opacity:0 } }
        @keyframes fw-float { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-6px) } }
        @keyframes fw-fade-up { from { opacity:0; transform: translateY(12px) } to { opacity:1; transform: translateY(0) } }
        @keyframes fw-scene-in { from { opacity:0; transform: translateY(24px) scale(.96) } to { opacity:1; transform: translateY(0) scale(1) } }
      `}</style>

      {/* Grain overlay for cinematic feel */}
      <div className="absolute inset-0 pointer-events-none opacity-[.06]" style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
      }} />

      {/* Top progress bar */}
      <div className="w-full max-w-[540px] px-4 pt-4 relative z-30" data-testid="promo-progress">
        <div className="flex gap-1">
          {SCENES.map((s, i) => {
            const isDone = i < sceneIdx;
            const isNow = i === sceneIdx;
            const pct = isDone ? 100 : isNow ? (elapsedInScene / s.duration) * 100 : 0;
            return (
              <button
                key={s.id}
                onClick={() => jumpTo(i)}
                data-testid={`promo-progress-${i}`}
                className="flex-1 h-1 rounded-full overflow-hidden relative"
                style={{ background: "rgba(255,255,255,.15)" }}
                aria-label={`Jump to scene ${i + 1}: ${s.label}`}
              >
                <div className="absolute inset-y-0 left-0" style={{ background: "#e6d59a", width: `${pct}%`, transition: "width .1s linear" }} />
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between mt-2 text-[10px] uppercase tracking-widest text-white/60">
          <span data-testid="promo-scene-label">{String(sceneIdx + 1).padStart(2, "0")} · {scene.label}</span>
          <span>{Math.round(overallPct)}%</span>
        </div>
      </div>

      {/* Main stage */}
      <div className="flex-1 w-full flex items-center justify-center relative z-10 py-6" data-testid="promo-stage">
        {/* Ambient glow behind phone that matches scene accent */}
        <div className="absolute pointer-events-none" style={{
          width: "80vw", maxWidth: "500px", aspectRatio: "1",
          background: `radial-gradient(circle, ${scene.accent}22 0%, transparent 55%)`,
          filter: "blur(30px)",
          transition: "background .8s ease",
        }} />

        <div
          key={scene.id}
          className="relative"
          style={{
            animation: "fw-float 4s ease-in-out infinite, fw-scene-in .55s cubic-bezier(.16,1,.3,1) both",
          }}
        >
          <PhoneFrame accent={scene.accent}>
            {Mockup ? <Mockup /> : null}
          </PhoneFrame>

          {/* Speech bubbles */}
          {scene.bubbles.map((b, i) => (
            <Bubble
              key={`${scene.id}-${i}`}
              text={b.text}
              position={b.position}
              visible={elapsedInScene >= (b.delay || 0)}
            />
          ))}
        </div>
      </div>

      {/* Bottom controls */}
      <div className="w-full max-w-[540px] px-4 pb-6 relative z-30" data-testid="promo-controls">
        <div className="flex items-center justify-between gap-2 mb-4">
          <button
            onClick={() => setPlaying((p) => !p)}
            data-testid="promo-play-pause"
            className="flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm active:scale-95 transition-transform"
            style={{ background: "rgba(255,255,255,.1)", color: "#fff" }}
          >
            {playing ? <><Pause size={14} weight="fill" /> Pause</> : <><Play size={14} weight="fill" /> Play</>}
          </button>
          <button
            onClick={share}
            data-testid="promo-share"
            className="flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm active:scale-95 transition-transform"
            style={{ background: "#e6d59a", color: "#143D2F" }}
          >
            <ShareNetwork size={14} weight="bold" />
            {copied ? "Copied!" : "Share this promo"}
          </button>
        </div>

        {/* CTA row */}
        <div className="flex flex-col sm:flex-row items-stretch gap-2">
          <Link
            to="/register"
            data-testid="promo-cta-register"
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full font-black text-sm active:scale-95 transition-transform"
            style={{ background: "#e6d59a", color: "#143D2F" }}
          >
            <Sparkle size={14} weight="fill" /> Try Fairway free
          </Link>
          <Link
            to="/login"
            data-testid="promo-cta-login"
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full font-bold text-sm active:scale-95 transition-transform"
            style={{ background: "rgba(255,255,255,.08)", color: "#fff" }}
          >
            Sign in
          </Link>
        </div>

        <p className="text-center text-[11px] text-white/50 mt-4 leading-relaxed">
          UK · Spain · Portugal · 3,500+ courses · Free forever · Pro unlocks Swing Lab
        </p>
      </div>

      {/* Corner watermark */}
      <div className="absolute top-4 right-4 z-30 flex items-center gap-1.5 opacity-70">
        <Flag size={14} weight="fill" style={{ color: "#e6d59a" }} />
        <span className="text-[11px] font-black tracking-tight" style={{ color: "#e6d59a" }}>Fairway</span>
      </div>
    </div>
  );
}
